<template>
  <v-menu :close-on-content-click="false" v-model="showMenu">
    <template #activator="{ props }">
      <v-btn
        variant="outlined"
        v-bind="props"
        rounded="0"
        prepend-icon="mdi-calendar"
        :color="color"
        class="mr-0 pl-2 pr-1"
        style="
          margin-left: 0.5rem !important;
          padding-left: 0.5rem !important;
          padding-right: 0.25rem !important;
        "
      >
        {{ modelValueText }}
      </v-btn>
    </template>
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <v-btn
          density="compact"
          icon="mdi-chevron-left"
          variant="plain"
          class="mr-2"
          @click="prevPage"
          :disabled="isPrevDisabled"
        />
        <v-btn-toggle
          v-model="bereich"
          mandatory
          color="success"
          density="compact"
          rounded="0"
        >
          <v-btn value="Monat" v-if="showMonth" @mousedown.stop>{{
            globalsBezeichnung(Globals.Begriff, "FX_Monat", "Monat")
          }}</v-btn>
          <v-btn value="Jahr" v-if="showYear" @mousedown.stop>{{
            globalsBezeichnung(Globals.Begriff, "FX_Jahr", "Jahr")
          }}</v-btn>
          <v-btn value="Tag" v-if="showDay" @mousedown.stop>
            {{ globalsBezeichnung(Globals.Begriff, "FX_Tag", "Tag") }}</v-btn
          >
        </v-btn-toggle>
        <v-btn
          density="compact"
          icon="mdi-chevron-right"
          variant="plain"
          class="ml-2"
          @click="nextPage"
          :disabled="isNextDisabled"
        />
      </v-card-title>
      <v-card-text class="pa-0">
        <div v-if="bereich === 'Jahr'">
          <v-list class="pa-0">
            <v-list-item
              density="compact"
              v-for="year in pagedYearsWithInactive"
              :key="year.value"
              :active="
                !year.inactive &&
                (selectedYear === year.value || isYearInRange(year.value))
              "
              :disabled="year.inactive"
              @click="!year.inactive && selectYear(year.value)"
              style="cursor: pointer"
              active-class="text-error"
            >
              <v-list-item-subtitle>{{ year.value }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </div>
        <div v-else-if="bereich === 'Monat'" max-width="200">
          <v-list class="pa-0">
            <v-list-item
              density="compact"
              v-for="item in pagedMonthItemsWithInactive"
              :key="item.year + '-' + item.monthIdx"
              :active="
                !item.inactive &&
                ((selectedYear === item.year && selectedMonth === item.monthIdx) ||
                  isMonthInRange(item))
              "
              :disabled="item.inactive"
              @click="!item.inactive && selectMonth(item.year, item.monthIdx)"
              style="cursor: pointer"
              active-class="text-success"
            >
              <template v-slot:append="{}">
                <v-icon
                  v-if="selectedMonth === item.monthIdx || isMonthInRange(item)"
                  color="success"
                  class="pr-2"
                  >mdi-check</v-icon
                >
              </template>
              <v-list-item-subtitle>
                {{ months[item.monthIdx] }}
                {{ item.year }}
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </div>
        <div v-else-if="bereich === 'Tag'">
          <v-date-picker
            v-model="selectedDate"
            :max="maxDate"
            :min="minDate"
            color="primary"
            show-adjacent-months
          />
        </div>
      </v-card-text>
      <v-card-actions class="pa-0">
        <v-spacer></v-spacer>

        <v-btn
          icon="mdi-close"
          rounded="0"
          variant="flat"
          color="blue darken-1"
          class="mr-2"
          @click="showMenu = false"
          density="comfortable"
        />
        <v-btn
          icon="mdi-check-circle-outline"
          rounded="0"
          variant="flat"
          color="green"
          class="mr-2"
          @click="acceptSelection"
          density="comfortable"
        />
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script setup lang="ts">
import AUApi from "@/api";
import { ref, watch, computed, nextTick } from "vue";
import { getMonthNames } from "@/editor/CalenderHelper";
import { globalsBezeichnung } from "@/local";
import { Globals } from "@/Globals";

const props = defineProps({
  minDate: {
    type: Date,
    default: () => new Date(new Date().getFullYear() - 5, 0, 1),
  },
  maxDate: {
    type: Date,
    default: () => new Date(new Date().getFullYear() + 2, 0, 1),
  },
  mode: { type: String, default: "Monat" },
  showDay: { type: Boolean, default: false },
  showMonth: { type: Boolean, default: true },
  showYear: { type: Boolean, default: true },
  range: { type: Boolean, default: false }, // NEW: enable range selection
  color: { type: String, default: "white" },
});
const selectedMonths = ref<string[]>([]);
const showMenu = ref(false);
const modelValue = defineModel<APIZeitbereich>();
//modelValue.value = AUApi.Current.zeitbereich; // böse Idee, da es sonst nicht reaktiv ist
const emit = defineEmits<{
  updateZeitbereich: APIZeitbereich[];
  "update:modelValue": (APIZeitbereich | undefined)[];
}>();

const now = new Date();
const minDate = computed(() => props.minDate);
const maxDate = computed(() => props.maxDate);

const months = getMonthNames();

const years = computed(() => {
  const minY = minDate.value.getFullYear() ?? new Date(now.getFullYear() - 10, 0, 1);
  const maxY = maxDate.value.getFullYear() ?? new Date(now.getFullYear() + 10, 0, 1);
  const arr = [];
  for (let y = minY; y <= maxY; y++) arr.push(y);
  return arr;
});

const yearsPerPage = 10;
const monthsPerPage = 12;

const yearPage = ref(1);
const monthPage = ref(1);

const yearPageCount = computed(() => Math.ceil(years.value.length / yearsPerPage));
const pagedYears = computed(() =>
  years.value.slice((yearPage.value - 1) * yearsPerPage, yearPage.value * yearsPerPage)
);
function isSelected(item: { year: number; monthIdx: number }) {
  const key = `${item.year}-${item.monthIdx.toString().padStart(2, "0")}`;
  return selectedMonths.value.includes(key);
}
// Add inactive flag for years outside min/max
const pagedYearsWithInactive = computed(() => {
  return pagedYears.value.map((year) => {
    const yearStart = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31, 23, 59, 59, 999);
    const inactive = yearEnd < minDate.value || yearStart > maxDate.value;
    return { value: year, inactive };
  });
});

// Flat list of all months in all years within min/max
const allMonthItems = computed(() => {
  const items: { year: number; monthIdx: number }[] = [];
  const minY = minDate.value.getFullYear();
  const maxY = maxDate.value.getFullYear();
  for (let y = minY; y <= maxY; y++) {
    for (let m = 0; m < 12; m++) {
      items.push({ year: y, monthIdx: m });
    }
  }
  return items;
});
const monthPageCount = computed(() =>
  Math.ceil(allMonthItems.value.length / monthsPerPage)
);
const pagedMonthItems = computed(() =>
  allMonthItems.value.slice(
    (monthPage.value - 1) * monthsPerPage,
    monthPage.value * monthsPerPage
  )
);

// Add inactive flag for months outside min/max
const pagedMonthItemsWithInactive = computed(() => {
  return pagedMonthItems.value.map((item) => {
    const monthStart = new Date(item.year, item.monthIdx, 1);
    const monthEnd = new Date(item.year, item.monthIdx + 1, 0, 23, 59, 59, 999);
    const inactive = monthEnd < minDate.value || monthStart > maxDate.value;
    return { ...item, inactive };
  });
});
const currentYear = ref(now.getFullYear());
const selectedYear = ref<number | null>(modelValue.value?.von?.getFullYear?.() ?? null);
const selectedMonth = ref<number | null>(modelValue.value?.von?.getMonth?.() ?? null);
const selectedDate = ref<Date | null>(modelValue.value?.von ?? null);

declare type APIZeitbereichBereich = "Jahr" | "Monat" | "Tag";

const bereich = ref<APIZeitbereichBereich>(
  (props.mode ?? modelValue.value?.bereich ?? "Jahr") as APIZeitbereichBereich
);

// Helper to set the correct page for year/month selection
function setPageToSelected() {
  // Jahr
  if (bereich.value === "Jahr") {
    let year = selectedYear.value ?? now.getFullYear();
    let idx = years.value.indexOf(year);
    yearPage.value = idx >= 0 ? Math.floor(idx / yearsPerPage) + 1 : 1;
  }
  // Monat
  if (bereich.value === "Monat") {
    let idx = -1;
    if (selectedYear.value != null && selectedMonth.value != null) {
      idx = allMonthItems.value.findIndex(
        (item) =>
          item.year === selectedYear.value && item.monthIdx === selectedMonth.value
      );
    }
    monthPage.value = idx >= 0 ? Math.floor(idx / monthsPerPage) + 1 : 1;
  }
}
const modelValueText = ref("Zeitraum wählen");
modelValueText.value = getModelValueText(modelValue.value);

// Halte die Anzeige synchron zum v-model
watch(
  () => modelValue.value,
  (val) => {
    modelValueText.value = getModelValueText(val);
    if (val?.von) {
      selectedYear.value = val.von.getFullYear?.() ?? null;
      selectedMonth.value = val.von.getMonth?.() ?? null;
      selectedDate.value = val.von ?? null;
      bereich.value = (val.bereich ?? bereich.value) as APIZeitbereichBereich;
      setPageToSelected();
    }
  },
  { immediate: true, deep: true }
);

function getModelValueText(val: APIZeitbereich | undefined = modelValue.value) {
  if (!val || !val.von) return "Zeitraum wählen";
  if (val.bereich === "Jahr") {
    return "Jahr " + val.von.getFullYear().toString();
  } else if (val.bereich === "Monat") {
    let textVon = months[val.von.getMonth()] + " " + val.von.getFullYear();
    const textBis = months[val.bis.getMonth()] + " " + val.bis.getFullYear();
    if (textVon !== textBis) {
      textVon = textVon + " - " + textBis;
    }
    return textVon;
  } else if (val.bereich === "Tag") {
    return val.von.toLocaleDateString();
  }
  return "Zeitraum wählen!";
}

watch(
  () => props.mode,
  (val) => {
    if (val && bereich.value !== val) {
      bereich.value = val as APIZeitbereichBereich;
    }
  }
);

watch(bereich, (val) => {
  //   if (props.mode && val !== props.mode) {
  //     bereich.value = props.mode;
  //     return;
  //   }
  nextTick(() => setPageToSelected());
  if (val === "Jahr") {
    if (selectedYear.value == null) selectedYear.value = now.getFullYear();
    // emit value only, do not update modelValue directly here
  } else if (val === "Monat") {
    if (selectedYear.value == null) selectedYear.value = now.getFullYear();
    if (selectedMonth.value == null) selectedMonth.value = now.getMonth();
    // emit value only, do not update modelValue directly here
  } else if (val === "Tag") {
    if (!selectedDate.value) selectedDate.value = now;
    // emit value only, do not update modelValue directly here
  }
});

watch([selectedYear, selectedMonth, selectedDate, bereich, currentYear], () => {});

function prevPage() {
  if (bereich.value === "Jahr") {
    if (yearPage.value > 1) yearPage.value--;
  } else if (bereich.value === "Monat") {
    if (monthPage.value > 1) monthPage.value--;
  }
}
function nextPage() {
  if (bereich.value === "Jahr") {
    if (yearPage.value < yearPageCount.value) yearPage.value++;
  } else if (bereich.value === "Monat") {
    if (monthPage.value < monthPageCount.value) monthPage.value++;
  }
}

const isPrevDisabled = computed(() => {
  if (bereich.value === "Jahr") {
    // Check if any year on previous page is enabled
    if (yearPage.value <= 1) return true;
    const prevPageYears = years.value.slice(
      (yearPage.value - 2) * yearsPerPage,
      (yearPage.value - 1) * yearsPerPage
    );
    return !prevPageYears.some((year) => {
      const yearStart = new Date(year, 0, 1);
      const yearEnd = new Date(year, 11, 31, 23, 59, 59, 999);
      return !(yearEnd < minDate.value || yearStart > maxDate.value);
    });
  } else if (bereich.value === "Monat") {
    if (monthPage.value <= 1) return true;
    const prevPageMonths = allMonthItems.value.slice(
      (monthPage.value - 2) * monthsPerPage,
      (monthPage.value - 1) * monthsPerPage
    );
    return !prevPageMonths.some((item) => {
      const monthStart = new Date(item.year, item.monthIdx, 1);
      const monthEnd = new Date(item.year, item.monthIdx + 1, 0, 23, 59, 59, 999);
      return !(monthEnd < minDate.value || monthStart > maxDate.value);
    });
  }
  return false;
});

const isNextDisabled = computed(() => {
  if (bereich.value === "Jahr") {
    if (yearPage.value >= yearPageCount.value) return true;
    const nextPageYears = years.value.slice(
      yearPage.value * yearsPerPage,
      (yearPage.value + 1) * yearsPerPage
    );
    return !nextPageYears.some((year) => {
      const yearStart = new Date(year, 0, 1);
      const yearEnd = new Date(year, 11, 31, 23, 59, 59, 999);
      return !(yearEnd < minDate.value || yearStart > maxDate.value);
    });
  } else if (bereich.value === "Monat") {
    if (monthPage.value >= monthPageCount.value) return true;
    const nextPageMonths = allMonthItems.value.slice(
      monthPage.value * monthsPerPage,
      (monthPage.value + 1) * monthsPerPage
    );
    return !nextPageMonths.some((item) => {
      const monthStart = new Date(item.year, item.monthIdx, 1);
      const monthEnd = new Date(item.year, item.monthIdx + 1, 0, 23, 59, 59, 999);
      return !(monthEnd < minDate.value || monthStart > maxDate.value);
    });
  }
  return false;
});

// Range selection state
const rangeStart = ref<{ year: number; monthIdx: number } | number | null>(null); // {year, monthIdx} for months, number for years
const rangeEnd = ref<{ year: number; monthIdx: number } | number | null>(null);

function isMonthInRange(item: { year: number; monthIdx: number }) {
  if (!props.range || !rangeStart.value) return false;
  // If only rangeStart is set (starting a new range), highlight it as active
  if (rangeStart.value && !rangeEnd.value) {
    if (typeof rangeStart.value !== "number") {
      return (
        item.year === rangeStart.value.year && item.monthIdx === rangeStart.value.monthIdx
      );
    }
    return false;
  }
  if (!rangeEnd.value) return false;
  if (typeof rangeStart.value === "number" || typeof rangeEnd.value === "number")
    return false;
  const startVal = rangeStart.value.year * 12 + rangeStart.value.monthIdx;
  const endVal = rangeEnd.value.year * 12 + rangeEnd.value.monthIdx;
  const itemVal = item.year * 12 + item.monthIdx;
  return itemVal >= Math.min(startVal, endVal) && itemVal <= Math.max(startVal, endVal);
}
function isYearInRange(year: number) {
  if (!props.range || rangeStart.value == null) return false;
  // If only rangeStart is set (starting a new range), highlight it as active
  if (rangeStart.value != null && rangeEnd.value == null) {
    if (typeof rangeStart.value === "number") {
      return year === rangeStart.value;
    }
    return false;
  }
  if (rangeEnd.value == null) return false;
  if (typeof rangeStart.value !== "number" || typeof rangeEnd.value !== "number")
    return false;
  return (
    year >= Math.min(rangeStart.value, rangeEnd.value) &&
    year <= Math.max(rangeStart.value, rangeEnd.value)
  );
}

function selectMonth(year: number, monthIdx: number) {
  if (!props.range) {
    selectedYear.value = year;
    selectedMonth.value = monthIdx;
    setPageToSelected();
    return;
  }
  // If starting a new range, clear old selection and set new start as active
  if (!rangeStart.value || (rangeStart.value && rangeEnd.value)) {
    rangeStart.value = { year, monthIdx };
    rangeEnd.value = null;
    selectedYear.value = year;
    selectedMonth.value = monthIdx;
  } else if (!rangeEnd.value) {
    rangeEnd.value = { year, monthIdx };
  }
}
function selectYear(year: number) {
  if (!props.range) {
    selectedYear.value = year;
    setPageToSelected();
    return;
  }
  // If starting a new range, clear old selection and set new start as active
  if (rangeStart.value == null || (rangeStart.value != null && rangeEnd.value != null)) {
    rangeStart.value = year;
    rangeEnd.value = null;
    selectedYear.value = year;
  } else if (rangeEnd.value == null) {
    rangeEnd.value = year;
  }
}

function acceptSelection() {
  let von: Date, bis: Date, selbereich: string;
  let newZeitbereich: APIZeitbereich | undefined;
  if (
    props.range &&
    bereich.value === "Monat" &&
    rangeStart.value &&
    rangeEnd.value &&
    typeof rangeStart.value !== "number" &&
    typeof rangeEnd.value !== "number"
  ) {
    // Range for months
    const start =
      rangeStart.value.year * 12 + rangeStart.value.monthIdx <
      rangeEnd.value.year * 12 + rangeEnd.value.monthIdx
        ? rangeStart.value
        : rangeEnd.value;
    const end =
      rangeStart.value.year * 12 + rangeStart.value.monthIdx <
      rangeEnd.value.year * 12 + rangeEnd.value.monthIdx
        ? rangeEnd.value
        : rangeStart.value;
    von = new Date(start.year, start.monthIdx, 1, 0, 0, 0, 0);
    bis = new Date(end.year, end.monthIdx + 1, 0, 23, 59, 59, 999);
    selbereich = "Monat";
  } else if (
    props.range &&
    bereich.value === "Jahr" &&
    typeof rangeStart.value === "number" &&
    typeof rangeEnd.value === "number"
  ) {
    // Range for years
    const start = Math.min(rangeStart.value, rangeEnd.value);
    const end = Math.max(rangeStart.value, rangeEnd.value);
    von = new Date(start, 0, 1, 0, 0, 0, 0);
    bis = new Date(end, 11, 31, 23, 59, 59, 999);
    selbereich = "Jahr";
  } else if (bereich.value === "Jahr" && selectedYear.value) {
    von = new Date(selectedYear.value, 0, 1);
    bis = new Date(selectedYear.value, 11, 31, 23, 59, 59, 999);
    selbereich = "Jahr";
  } else if (
    bereich.value === "Monat" &&
    selectedYear.value !== null &&
    selectedMonth.value !== null
  ) {
    von = new Date(selectedYear.value, selectedMonth.value, 1);
    bis = new Date(selectedYear.value, selectedMonth.value + 1, 0, 23, 59, 59, 999);
    selbereich = "Monat";
  } else {
    von = new Date(selectedDate.value!);
    bis = new Date(selectedDate.value!);
    bis.setHours(23, 59, 59, 999);
    selbereich = "Tag";
  }
  von = new Date(von.getFullYear(), von.getMonth(), von.getDate(), 0, 0, 0, 0);
  bis = new Date(bis.getFullYear(), bis.getMonth(), bis.getDate(), 23, 59, 59, 999);
  newZeitbereich = {
    von,
    bis,
    bereich: selbereich,
  };
  modelValue.value = newZeitbereich;
  emit("updateZeitbereich", newZeitbereich!);
  emit("update:modelValue", newZeitbereich);
  modelValueText.value = getModelValueText(newZeitbereich);
  modelValue.value = newZeitbereich;
  showMenu.value = false;
  // Reset range after accept
  rangeStart.value = null;
  rangeEnd.value = null;
}

setPageToSelected();
</script>
<style scoped>
.v-btn-toggle .v-btn {
  font-size: 14px !important;
}
</style>
